import {
    FC,
    FormEvent,
    useRef,
    useState,
} from 'react'

import { Button, Icon, InputLabel } from '../../../components'
import { DistrictDivisionDistrictTemplate, FormActionButtons, TextInput } from '../../../compositions'
import { DistrictDivisionDistrictTemplateForm } from '../../../entities/DistrictDivisionDistrictTemplate/DistrictDivisionDistrictTemplate'
import { DistrictDivisionGroupTemplate, EditDistrictDivisionGroupTemplateFormData } from '../../../entities/DistrictDivisionGroupTemplate/DistrictDivisionGroupTemplate'
import {
    transformDistrictDivisionDistrictTemplateFormToDistrictDivisionDistrictTemplate,
    transformDistrictDivisionDistrictTemplateToDistrictDivisionDistrictTemplateForm,
} from '../../../entities/DistrictDivisionGroupTemplate/DistrictDivisionGroupTemplateTransformers'
import { getUniqueArrayList } from '../../../helpers/array'
import trans from '../../../helpers/trans'
import { ModalFormProps } from '../../../types/modalFormTypes'
import { EditDistrictDivisionGroupTemplateFormErrors, validateEditDistrictDivisionGroupTemplateFormData } from './validations/editDistrictDivisionGroupTemplateFormValidation'

import './EditDistrictDivisionGroupTemplateForm.scss'

interface EditDistrictDivisionGroupTemplateFormProps extends ModalFormProps<EditDistrictDivisionGroupTemplateFormData> {
    districtDivisionGroupTemplate: DistrictDivisionGroupTemplate;
    districtDivisionTemplateId: string;
    onSubmit: (editDistrictDivisionGroupTemplateFormData: EditDistrictDivisionGroupTemplateFormData) => void;
}

const EditDistrictDivisionGroupTemplateForm: FC<EditDistrictDivisionGroupTemplateFormProps> = ({
    isLoading,
    districtDivisionGroupTemplate,
    districtDivisionTemplateId,
    onSubmit,
    onCancel,
}) => {
    const [formData, setFormData] = useState<EditDistrictDivisionGroupTemplateFormData>({
        id: districtDivisionGroupTemplate.id,
        name: districtDivisionGroupTemplate.name,
        districtDivisionDistrictTemplates: districtDivisionGroupTemplate.districtDivisionDistrictTemplates,
        districtDivisionTemplateId,
        order: districtDivisionGroupTemplate.order,
    })
    const [formErrors, setFormErrors] = useState<EditDistrictDivisionGroupTemplateFormErrors>({})
    const [groupName, setGroupName] = useState<string>(formData.name)
    const [districtDivisionDistrictTemplates, setDistrictDivisionDistrictTemplates] = useState<DistrictDivisionDistrictTemplateForm[]>(
        transformDistrictDivisionDistrictTemplateToDistrictDivisionDistrictTemplateForm(districtDivisionGroupTemplate.districtDivisionDistrictTemplates),
    )

    const formRef = useRef<HTMLFormElement>(null)

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const formDataToSubmit = {
            id: formData.id,
            name: groupName,
            districtDivisionDistrictTemplates: transformDistrictDivisionDistrictTemplateFormToDistrictDivisionDistrictTemplate(districtDivisionDistrictTemplates),
            districtDivisionTemplateId,
            order: districtDivisionGroupTemplate.order,
        }

        setFormData(formDataToSubmit)

        const [errors, hasErrors] = validateEditDistrictDivisionGroupTemplateFormData(formDataToSubmit)
        setFormErrors(errors)

        if (! hasErrors) {
            onSubmit(formDataToSubmit)
        }
    }

    const handleAddDistrictDivisionDistrict = (): void => {
        const newDistrict: DistrictDivisionDistrictTemplateForm = {
            id: new Date().getTime().toString(),
            name: '',
            maxAmountOfEmployees: 1,
            order: new Date().getTime(),
        }

        setDistrictDivisionDistrictTemplates([...districtDivisionDistrictTemplates, newDistrict])
    }

    const handleDistrictNameChange = (id: string, name: string): void => {
        const districtDivisionDistrictTemplate = districtDivisionDistrictTemplates.find(districtDivision => districtDivision.id === id)
        if (districtDivisionDistrictTemplate) {
            districtDivisionDistrictTemplate.name = name

            const newDistrictDivisionDistrictTemplates = getUniqueArrayList([districtDivisionDistrictTemplate, ...districtDivisionDistrictTemplates], 'id')

            setDistrictDivisionDistrictTemplates(newDistrictDivisionDistrictTemplates)
        }
    }

    const handleDistrictMaxAmountOfEmployeesChange = (id: string, maxAmountOfEmployees: number): void => {
        const districtDivisionDistrictTemplate = districtDivisionDistrictTemplates.find(districtDivision => districtDivision.id === id)
        if (districtDivisionDistrictTemplate) {
            districtDivisionDistrictTemplate.maxAmountOfEmployees = maxAmountOfEmployees

            const newDistrictDivisionDistrictTemplates = getUniqueArrayList([districtDivisionDistrictTemplate, ...districtDivisionDistrictTemplates], 'id')

            setDistrictDivisionDistrictTemplates(newDistrictDivisionDistrictTemplates)
        }
    }

    const handleDeleteDistrict = (id: string): void => {
        const newDistrictDivisionDistrictTemplates = districtDivisionDistrictTemplates.filter(districtDivision => districtDivision.id !== id)

        setDistrictDivisionDistrictTemplates(newDistrictDivisionDistrictTemplates)
    }

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="edit-district-division-group-template-form">
            <div className="edit-district-division-group-template-form__row">
                <div className="edit-district-division-group-template-form__group-name-input">
                    <TextInput
                        label={trans('containers.forms.addDistrictDivisionGroupTemplateForm.groupName')}
                        type="text"
                        value={groupName}
                        error={formErrors.name}
                        onChange={setGroupName}
                    />
                </div>
            </div>

            {districtDivisionDistrictTemplates.length > 0 && (
                <div className="edit-district-division-group-template-form__input-labels">
                    <div className="edit-district-division-group-template-form__label">
                        <InputLabel text={trans('containers.forms.addDistrictDivisionGroupTemplateForm.districtName')} />
                    </div>
                    <div className="edit-district-division-group-template-form__label">
                        <InputLabel text={trans('containers.forms.addDistrictDivisionGroupTemplateForm.maxAmountOfEmployees')} />
                    </div>
                </div>
            )}

            {districtDivisionDistrictTemplates
                .sort((a, b) => a.order - b.order)
                .map(districtDivisionDistrictTemplate => (
                    <DistrictDivisionDistrictTemplate
                        key={districtDivisionDistrictTemplate.id}
                        districtDivisionDistrictTemplate={districtDivisionDistrictTemplate}
                        formErrors={formErrors}
                        onDistrictNameChange={handleDistrictNameChange}
                        onDistrictMaxAmountOfEmployeesChange={handleDistrictMaxAmountOfEmployeesChange}
                        onDeleteDistrict={handleDeleteDistrict}
                        className="edit-district-division-group-template-form__template"
                    />
                ))}

            <div className="edit-district-division-group-template-form__row">
                <div className="edit-district-division-group-template-form__col">
                    <Button
                        text={trans('containers.forms.addDistrictDivisionGroupTemplateForm.addDistrictDivisionGroupDistrict')}
                        onClick={handleAddDistrictDivisionDistrict}
                        className="edit-district-division-group-template-form__add-district-button"
                    >
                        <Icon
                            name="plus-circle"
                            className="edit-district-division-group-template-form__add-district-button-icon"
                        />
                        <p className="edit-district-division-group-template-form__add-district-button-label">
                            {trans('containers.forms.addDistrictDivisionGroupTemplateForm.addDistrictDivisionGroupDistrict')}
                        </p>
                    </Button>
                </div>
            </div>

            <FormActionButtons
                isLoading={isLoading}
                disabled={! groupName}
                onCancelClick={onCancel}
            />
        </form>
    )
}

export default EditDistrictDivisionGroupTemplateForm
